import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLaptop, faVideo, faVolumeHigh, faClock, faCircleCheck, faRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import './joinfreeclass.css';
import useOnlineStatus, { NO_INTERNET_MSG, SERVER_MSG, REQUEST_TIMEOUT_MS, wakeServer, reasonOf, withRetry } from './useOnlineStatus';

const CODE_LENGTH = 6;

const CHECKLIST = [
  { icon: faLaptop, text: 'A laptop or computer, charged or plugged in' },
  { icon: faVideo, text: 'Zoom installed, with camera and microphone working' },
  { icon: faVolumeHigh, text: 'A quiet place where your child can focus' },
  { icon: faClock, text: 'Join a few minutes early to settle in' },
];

const JoinFreeClass = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorReason, setErrorReason] = useState(''); // 'enrolled' / 'expired' (show the right button)
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const online = useOnlineStatus();

  // wake the server while the parent types the code
  useEffect(() => {
    wakeServer();
  }, []);

  // Clear the "no internet" message as soon as the connection comes back
  useEffect(() => {
    if (online) setErrorMessage((prev) => (prev === NO_INTERNET_MSG ? '' : prev));
  }, [online]);
  const shownError = errorMessage || (!online ? NO_INTERNET_MSG : '');
  const EMAIL = process.env.REACT_APP_EMAIL;
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;

  const whatsappUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    "Hi! I booked a free trial class but I don't have my access code."
  )}&type=phone_number&app_absent=0`;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setErrorReason('');

    if (code.length !== CODE_LENGTH) {
      setErrorMessage(`Please enter the ${CODE_LENGTH}-character code we sent you.`);
      return;
    }

    if (!navigator.onLine) {
      setErrorMessage(NO_INTERNET_MSG);
      return;
    }

    setLoading(true);
    try {
      const response = await withRetry(
        () => axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/verify-code`, { code }, { timeout: REQUEST_TIMEOUT_MS }),
        (err) => !err.response
      );
      if (response.data.success) {
        setSuccess(true);
        sessionStorage.setItem('demoStudentId', response.data.studentId);
        setTimeout(() => navigate('/DemoClass'), 1200);
      } else {
        setErrorMessage(response.data.message || 'This code didn’t work. Please check it and try again.');
      }
    } catch (error) {
      if (!error.response) {
        // No answer at all. Only say "no internet" when the device really is offline;
        // otherwise it's our server (waking up, slow or blocked).
        console.error('Join free class: no answer from the server', error);
        setErrorMessage(navigator.onLine ? `${SERVER_MSG} (${reasonOf(error)})` : NO_INTERNET_MSG);
      } else {
        setErrorMessage(error.response.data?.message || 'We couldn’t check the code right now. Please try again.');
        setErrorReason(error.response.data?.reason || '');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rmjoin" aria-labelledby="rmjoin-title">
      <div className="rmjoin-inner">
        {/* Code form (first on every screen size) */}
        <form className="rmjoin-card" onSubmit={handleFormSubmit} noValidate>
          <span className="rmjoin-eyebrow">Free trial class</span>
          <h1 id="rmjoin-title" className="rmjoin-title">
            Join your <span>free class</span>
          </h1>
          <p className="rmjoin-sub">Enter the code we sent you by WhatsApp or email.</p>

          {success ? (
            <div className="rmjoin-success" role="status">
              <FontAwesomeIcon icon={faCircleCheck} />
              Code verified! Taking you to your class…
            </div>
          ) : (
            <>
              <label htmlFor="rmjoin-code" className="rmjoin-label">
                Access code
              </label>
              <input
                id="rmjoin-code"
                name="classCode"
                className={`rmjoin-input ${shownError ? 'has-error' : ''}`}
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck="false"
                maxLength={CODE_LENGTH}
                placeholder="ABC123"
                value={code}
                aria-invalid={Boolean(shownError)}
                aria-describedby={shownError ? 'rmjoin-error' : undefined}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\s/g, '').toUpperCase());
                  setErrorMessage('');
                  setErrorReason('');
                }}
              />

              {shownError && (
                <p id="rmjoin-error" className="rmjoin-error" role="alert">
                  {shownError}
                </p>
              )}
              {errorReason === 'enrolled' && (
                <Link to="/Login" className="rmjoin-next">
                  <FontAwesomeIcon icon={faRightToBracket} /> Go to log in
                </Link>
              )}
              {errorReason === 'expired' && (
                <a
                  className="rmjoin-next rmjoin-next--wa"
                  href={`https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent('Hi! Our free class code has expired. We’d like to book a new free class or enroll.')}&type=phone_number&app_absent=0`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faWhatsapp} /> Message us on WhatsApp
                </a>
              )}

              <button type="submit" className="rmjoin-btn" disabled={loading}>
                {loading ? (
                  <span className="rmjoin-spinner" aria-label="Checking code" />
                ) : (
                  <>
                    Join my class
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>
            </>
          )}

          <div className="rmjoin-help">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="rmjoin-help-link">
              <FontAwesomeIcon icon={faWhatsapp} />
              No code? Message us
            </a>
            {EMAIL && (
              <span>
                or email <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              </span>
            )}
          </div>
        </form>

        {/* Getting ready */}
        <aside className="rmjoin-ready" aria-labelledby="rmjoin-ready-title">
          <h2 id="rmjoin-ready-title" className="rmjoin-ready-title">
            Before the class
          </h2>
          <ul className="rmjoin-list">
            {CHECKLIST.map((item) => (
              <li key={item.text}>
                <span className="rmjoin-list-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                {item.text}
              </li>
            ))}
          </ul>
          <p className="rmjoin-note">
            Haven’t booked yet? <Link to="/FreeTrial">Book a free trial class</Link>
          </p>

          <Link to="/login" className="rmjoin-switch">
            <span className="rmjoin-switch-icon">
              <FontAwesomeIcon icon={faRightToBracket} />
            </span>
            <span className="rmjoin-switch-text">
              <strong>Already enrolled?</strong>
              <span>Students sign in here</span>
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="rmjoin-switch-arrow" />
          </Link>
        </aside>
      </div>
    </section>
  );
};

export default JoinFreeClass;